import { supabase } from '@/lib/supabase';

export type AssetType = 'image' | 'video' | 'social_profile' | 'social_post' | 'reference_url';
export type AssetStatus = 'pending' | 'analyzing' | 'analyzed' | 'failed';
export type PostStatus = 'draft' | 'approved' | 'scheduled' | 'published' | 'failed';
export type WeekStatus = 'draft' | 'approved' | 'scheduled' | 'published';

export interface BrandAsset {
  id: string;
  asset_type: AssetType;
  title?: string | null;
  url: string;
  storage_path?: string | null;
  source_platform?: string | null;
  notes?: string | null;
  tags?: string[];
  analysis?: any;
  analyzed_at?: string | null;
  status: AssetStatus;
  created_at: string;
}

export interface BrandVoiceProfile {
  id: string;
  name: string;
  summary: string;
  tone: string[];
  vocabulary: string[];
  avoid_words: string[];
  themes: string[];
  visual_palette: string[];
  visual_style: string;
  signature_phrases: string[];
  hashtag_strategy: string[];
  example_captions: string[];
  source_asset_ids: string[];
  raw_analysis: any;
  is_active: boolean;
  created_at: string;
}

export interface GeneratedWeek {
  id: string;
  brand_voice_id: string | null;
  week_label: string | null;
  status: WeekStatus;
  notes?: string | null;
  created_at: string;
}

export type ApprovalStatus = 'pending' | 'approved' | 'rejected';

export interface ScheduledPost {
  id: string;
  week_id: string | null;
  brand_voice_id: string | null;
  day_label: string | null;
  scheduled_for: string | null;
  platform: string;
  post_type: string;
  theme: string | null;
  caption: string | null;
  hashtags: string[];
  cta: string | null;
  image_url: string | null;
  image_prompt: string | null;
  video_url: string | null;
  status: PostStatus;
  order_index: number;
  approval_status: ApprovalStatus;
  admin_feedback: string | null;
  reviewed_at: string | null;
  reviewed_by: string | null;
  created_at: string;
}

// ---------- Assets ----------

export async function listAssets(): Promise<BrandAsset[]> {
  const { data } = await supabase
    .from('brand_assets')
    .select('*')
    .order('created_at', { ascending: false });
  return (data as BrandAsset[]) || [];
}

export async function uploadAssetFile(file: File): Promise<{ url: string; path: string } | null> {
  const ext = file.name.split('.').pop() || 'bin';
  const path = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const { error } = await supabase.storage.from('brand-assets').upload(path, file, {
    cacheControl: '3600',
    upsert: false,
  });
  if (error) return null;
  const { data } = supabase.storage.from('brand-assets').getPublicUrl(path);
  return { url: data.publicUrl, path };
}

export async function createAsset(asset: Partial<BrandAsset>): Promise<BrandAsset | null> {
  const { data, error } = await supabase
    .from('brand_assets')
    .insert({
      asset_type: asset.asset_type,
      title: asset.title || null,
      url: asset.url,
      storage_path: asset.storage_path || null,
      source_platform: asset.source_platform || null,
      notes: asset.notes || null,
      tags: asset.tags || [],
      status: 'pending',
    })
    .select('*')
    .single();
  if (error) return null;
  return data as BrandAsset;
}

export async function deleteAsset(id: string): Promise<void> {
  await supabase.from('brand_assets').delete().eq('id', id);
}

// ---------- Brand voice ----------

export async function listVoices(): Promise<BrandVoiceProfile[]> {
  const { data } = await supabase
    .from('brand_voice_profiles')
    .select('*')
    .order('created_at', { ascending: false });
  return (data as BrandVoiceProfile[]) || [];
}

export async function analyzeAssets(assetIds: string[], voiceName?: string): Promise<BrandVoiceProfile | null> {
  if (assetIds.length === 0) throw new Error('Select at least one asset to learn from.');
  const { data: assets } = await supabase
    .from('brand_assets')
    .select('*')
    .in('id', assetIds);
  if (!assets || assets.length === 0) throw new Error('Selected assets could not be loaded. Try refreshing.');

  // Mark as analyzing
  await supabase.from('brand_assets').update({ status: 'analyzing' }).in('id', assetIds);

  const { data, error } = await supabase.functions.invoke('analyze-brand-voice', {
    body: {
      assets: assets.map((a: any) => ({
        id: a.id,
        asset_type: a.asset_type,
        title: a.title,
        url: a.url,
        source_platform: a.source_platform,
        notes: a.notes,
      })),
      voice_name: voiceName,
    },
  });

  if (error || !data?.voice) {
    await supabase.from('brand_assets').update({ status: 'failed' }).in('id', assetIds);
    const msg =
      (data && (data.error as string)) ||
      (error && (error.message as string)) ||
      'The AI analysis service did not return a result. Please try again.';
    throw new Error(msg);
  }

  // Save the voice profile
  const { data: saved, error: saveErr } = await supabase
    .from('brand_voice_profiles')
    .insert({
      ...data.voice,
      is_active: true,
    })
    .select('*')
    .single();

  if (saveErr || !saved) {
    await supabase.from('brand_assets').update({ status: 'failed' }).in('id', assetIds);
    throw new Error(saveErr?.message || 'The brand voice was generated but could not be saved.');
  }

  // Mark assets as analyzed
  await supabase
    .from('brand_assets')
    .update({ status: 'analyzed', analyzed_at: new Date().toISOString() })
    .in('id', assetIds);

  return saved as BrandVoiceProfile;
}


export async function deleteVoice(id: string): Promise<void> {
  await supabase.from('brand_voice_profiles').delete().eq('id', id);
}

// ---------- Week generation ----------

export async function generateWeek(
  voice: BrandVoiceProfile,
  weekLabel?: string,
  generateImages = false,
): Promise<{ week: GeneratedWeek; posts: ScheduledPost[] } | null> {
  const { data, error } = await supabase.functions.invoke('generate-week-from-voice', {
    body: { voice, week_label: weekLabel, generate_images: generateImages },
  });
  if (error || !data?.posts) return null;

  const { data: weekRow } = await supabase
    .from('generated_content_weeks')
    .insert({
      brand_voice_id: voice.id,
      week_label: data.week_label || weekLabel || 'Generated Week',
      status: 'draft',
    })
    .select('*')
    .single();
  if (!weekRow) return null;

  // Compute scheduled_for from day_label + scheduled_time, starting next Monday
  const nextMonday = (() => {
    const d = new Date();
    const day = d.getDay();
    const diff = day === 0 ? 1 : 8 - day;
    d.setDate(d.getDate() + diff);
    d.setHours(9, 0, 0, 0);
    return d;
  })();
  const dayMap: Record<string, number> = {
    Monday: 0, Tuesday: 1, Wednesday: 2, Thursday: 3, Friday: 4, Saturday: 5, Sunday: 6,
  };

  const rows = (data.posts as any[]).map((p, i) => {
    const dayOffset = dayMap[p.day_label] ?? i;
    const scheduled = new Date(nextMonday);
    scheduled.setDate(scheduled.getDate() + dayOffset);
    // Parse time like "9:00 AM"
    const m = String(p.scheduled_time || '').match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
    if (m) {
      let h = parseInt(m[1], 10);
      const min = parseInt(m[2], 10);
      const ampm = (m[3] || '').toUpperCase();
      if (ampm === 'PM' && h < 12) h += 12;
      if (ampm === 'AM' && h === 12) h = 0;
      scheduled.setHours(h, min, 0, 0);
    }
    return {
      week_id: weekRow.id,
      brand_voice_id: voice.id,
      day_label: p.day_label || null,
      scheduled_for: scheduled.toISOString(),
      platform: 'instagram',
      post_type: p.post_type || 'Feed Post',
      theme: p.theme || null,
      caption: p.caption || null,
      hashtags: Array.isArray(p.hashtags) ? p.hashtags : [],
      cta: p.cta || null,
      image_url: p.image_url || null,
      image_prompt: p.image_prompt || null,
      status: 'draft' as PostStatus,
      order_index: i,
    };
  });

  const { data: postRows } = await supabase
    .from('scheduled_posts')
    .insert(rows)
    .select('*');

  return {
    week: weekRow as GeneratedWeek,
    posts: (postRows as ScheduledPost[]) || [],
  };
}

// ---------- Scheduled posts ----------

export async function listScheduledPosts(weekId?: string): Promise<ScheduledPost[]> {
  let q = supabase.from('scheduled_posts').select('*').order('scheduled_for', { ascending: true });
  if (weekId) q = q.eq('week_id', weekId);
  const { data } = await q;
  return (data as ScheduledPost[]) || [];
}

export async function listWeeks(): Promise<GeneratedWeek[]> {
  const { data } = await supabase
    .from('generated_content_weeks')
    .select('*')
    .order('created_at', { ascending: false });
  return (data as GeneratedWeek[]) || [];
}

export async function updateScheduledPost(id: string, patch: Partial<ScheduledPost>): Promise<void> {
  const allowed: any = {};
  ['day_label', 'scheduled_for', 'platform', 'post_type', 'theme', 'caption', 'hashtags', 'cta', 'image_url', 'image_prompt', 'status', 'order_index', 'approval_status', 'admin_feedback', 'reviewed_at', 'reviewed_by'].forEach((k) => {
    if (k in patch) allowed[k] = (patch as any)[k];
  });
  allowed.updated_at = new Date().toISOString();
  await supabase.from('scheduled_posts').update(allowed).eq('id', id);
}

export async function deleteScheduledPost(id: string): Promise<void> {
  await supabase.from('scheduled_posts').delete().eq('id', id);
}

/**
 * Admin approves a post. Marks it approved and ready for scheduling/publishing.
 */
export async function approvePost(id: string, reviewer: string, feedback?: string): Promise<void> {
  await updateScheduledPost(id, {
    approval_status: 'approved',
    admin_feedback: feedback?.trim() ? feedback.trim() : null,
    reviewed_at: new Date().toISOString(),
    reviewed_by: reviewer,
    status: 'approved',
  });
}

/**
 * Admin disapproves a post. Records feedback and sends the post back a step
 * (status -> 'draft') so the team can revise it in Step 3.
 */
export async function rejectPost(id: string, reviewer: string, feedback: string): Promise<void> {
  await updateScheduledPost(id, {
    approval_status: 'rejected',
    admin_feedback: feedback.trim(),
    reviewed_at: new Date().toISOString(),
    reviewed_by: reviewer,
    status: 'draft',
  });
}

/**
 * Generate a brand-voice-aware image for a single post that is missing one,
 * upload it to storage, persist it on the post, and return the public URL.
 */
export async function generatePostImage(
  post: ScheduledPost,
  voice: BrandVoiceProfile | null,
): Promise<string> {
  const { data, error } = await supabase.functions.invoke('generate-post-image', {
    body: {
      post: {
        caption: post.caption,
        theme: post.theme,
        post_type: post.post_type,
        day_label: post.day_label,
        image_prompt: post.image_prompt,
      },
      voice: voice
        ? {
            visual_style: voice.visual_style,
            visual_palette: voice.visual_palette,
            themes: voice.themes,
            tone: voice.tone,
          }
        : null,
    },
  });

  if (error || !data?.image_url) {
    throw new Error((data && (data.error as string)) || error?.message || 'Image generation failed.');
  }

  const dataUrl: string = data.image_url;

  // Convert the returned base64 data URL into a stored, durable public URL.
  let publicUrl = dataUrl;
  try {
    if (dataUrl.startsWith('data:')) {
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      const path = `generated/${post.id}-${Date.now()}.png`;
      const { error: upErr } = await supabase.storage
        .from('brand-assets')
        .upload(path, blob, { cacheControl: '3600', upsert: true, contentType: blob.type || 'image/png' });
      if (!upErr) {
        const { data: pub } = supabase.storage.from('brand-assets').getPublicUrl(path);
        if (pub?.publicUrl) publicUrl = pub.publicUrl;
      }
    }
  } catch {
    // Fall back to the raw data URL if upload fails.
  }

  await updateScheduledPost(post.id, {
    image_url: publicUrl,
    image_prompt: data.prompt || post.image_prompt,
  });

  return publicUrl;
}


export async function deleteWeek(id: string): Promise<void> {
  await supabase.from('generated_content_weeks').delete().eq('id', id);
}
